import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Tooltip,
  InputAdornment,
  Popover,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../../../../i18n';
import { BRAND_COLORS, alpha } from '../../../../../styles/theme';
import { InventoryLocation, InventoryItem } from '../../../../../types/api';
import { buildLocationPath } from './inventoryTree.utils';

interface InventoryItemsTableProps {
  items: InventoryItem[];
  locations: InventoryLocation[];
  selectedLocationId: string | null;
  search: string;
  onSearchChange: (value: string) => void;
  onAddItem: () => void;
  onEditItem: (item: InventoryItem) => void;
  onDeleteItem: (item: InventoryItem) => void;
  onNavigateToLocation: (locationId: string) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

export const InventoryItemsTable: React.FC<InventoryItemsTableProps> = ({
  items,
  locations,
  selectedLocationId,
  search,
  onSearchChange,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onNavigateToLocation,
  t,
}) => {
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Path popover state
  const [pathPopoverAnchor, setPathPopoverAnchor] = useState<HTMLElement | null>(null);
  const [pathPopoverLocationId, setPathPopoverLocationId] = useState<string | null>(null);

  // Reset page when items/filters change
  React.useEffect(() => {
    setPage(0);
  }, [items]);

  React.useEffect(() => {
    setPage(0);
  }, [selectedLocationId, search]);

  const paginatedItems = useMemo(
    () => items.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [items, page, rowsPerPage]
  );

  const handlePathClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, locationId: string) => {
      setPathPopoverAnchor(event.currentTarget);
      setPathPopoverLocationId(locationId);
    },
    []
  );

  const handleNavigate = useCallback(
    (locationId: string) => {
      onNavigateToLocation(locationId);
      setPathPopoverAnchor(null);
      setPathPopoverLocationId(null);
    },
    [onNavigateToLocation]
  );

  const pathForPopover = useMemo(() => {
    if (!pathPopoverLocationId) return [];
    return buildLocationPath(pathPopoverLocationId, locations);
  }, [pathPopoverLocationId, locations]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Controls row */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={t('inventory.search')}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ width: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={onAddItem}
          sx={{
            backgroundColor: BRAND_COLORS.forestGreen.main,
            '&:hover': { backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.85) },
          }}
        >
          {t('inventory.addItem')}
        </Button>
      </Box>

      {/* Items table */}
      <TableContainer component={Paper} variant="outlined" sx={{ flex: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>{t('inventory.name')}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 80 }} align="right">
                {t('inventory.quantity')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: 140 }}>{t('inventory.category')}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 180 }}>{t('inventory.location')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('inventory.notes')}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 100 }} align="center">
                {t('inventory.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedLocationId ? t('inventory.noItemsInLocation') : t('inventory.noItems')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedItems.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell>{item.category ?? '—'}</TableCell>
                  <TableCell>
                    {item.location_id && item.location_name ? (
                      <Tooltip title={t('inventory.locationPath')}>
                        <Chip
                          label={item.location_name}
                          size="small"
                          variant="outlined"
                          onClick={(e) => handlePathClick(e, item.location_id as string)}
                          sx={{ cursor: 'pointer', maxWidth: 160 }}
                        />
                      </Tooltip>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {item.notes ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('inventory.editItem')}>
                      <IconButton size="small" onClick={() => onEditItem(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('inventory.deleteItem')}>
                      <IconButton size="small" onClick={() => onDeleteItem(item)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {items.length > 0 && (
          <TablePagination
            component="div"
            count={items.length}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </TableContainer>

      {/* Location path popover */}
      <Popover
        open={!!pathPopoverAnchor}
        anchorEl={pathPopoverAnchor}
        onClose={() => {
          setPathPopoverAnchor(null);
          setPathPopoverLocationId(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box
          sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}
        >
          {pathForPopover.map((loc, idx) => (
            <React.Fragment key={loc.id}>
              {idx > 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mx: 0.25 }}>
                  {'>'}
                </Typography>
              )}
              <Chip
                label={loc.name}
                size="small"
                onClick={() => handleNavigate(loc.id)}
                sx={{ cursor: 'pointer' }}
              />
            </React.Fragment>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};
